import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlaylistInput, PublicPlaylist } from '../types';
import { api } from './client';
import { qk } from './keys';

export function usePlaylists() {
  return useQuery({
    queryKey: qk.playlists(),
    queryFn: () => api.get<{ items: PublicPlaylist[] }>('/admin/playlists'),
  });
}

export function useCreatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlaylistInput) => api.post<PublicPlaylist>('/admin/playlists', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlists'] }),
  });
}

export function useUpdatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: Partial<PlaylistInput> }) =>
      api.patch<PublicPlaylist>(`/admin/playlists/${args.id}`, args.input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlists'] }),
  });
}

/**
 * Re-fetches title / description / thumbnail / itemCount from YouTube.
 * Useful when an editor renames the playlist on YouTube and wants the
 * mobile cards to follow.
 */
export function useRefreshPlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PublicPlaylist>(`/admin/playlists/${id}/refresh`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlists'] }),
  });
}

export function useDeletePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ removed: number }>(`/admin/playlists/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlists'] }),
  });
}

export function useBulkDeletePlaylists() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ deleted: number }>('/admin/playlists/bulk-delete', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlists'] }),
  });
}
